import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Palette, User, Building2, Users } from 'lucide-react';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('');
  const [artistType, setArtistType] = useState('');
  const { login, register } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    location: '',
    bio: '',
    studio_id: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(language === 'es' ? '¡Bienvenido de vuelta!' : 'Welcome back!');
      } else {
        if (!userType) {
          toast.error(language === 'es' ? 'Selecciona un tipo de cuenta' : 'Select an account type');
          setLoading(false);
          return;
        }
        if (userType === 'artist' && !artistType) {
          toast.error(language === 'es' ? 'Selecciona tu tipo de tatuador' : 'Select your artist type');
          setLoading(false);
          return;
        }
        const finalUserType = userType === 'client' ? 'client' : artistType;
        await register({ ...formData, user_type: finalUserType });
        toast.success(language === 'es' ? '¡Cuenta creada exitosamente!' : 'Account created successfully!');
      }
      // Navigate immediately - state is already updated
      navigate('/feed', { replace: true });
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error de autenticación' : 'Authentication error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12">
        <Link to="/" className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-12">
          <ArrowLeft size={18} />
          <span className="text-sm uppercase tracking-wider">Volver</span>
        </Link>

        <div className="max-w-md">
          <h1 className="font-['Cinzel'] text-4xl md:text-5xl font-bold text-white mb-2" data-testid="auth-title">
            {isLogin ? 'BIENVENIDO' : 'ÚNETE'}
          </h1>
          <p className="text-zinc-500 mb-10">
            {isLogin ? 'Ingresa a tu cuenta para continuar' : 'Crea una cuenta para empezar'}
          </p>

          {!isLogin && !userType && (
            <div className="mb-10">
              <p className="text-sm uppercase tracking-wider text-zinc-500 mb-6">
                {language === 'es' ? '¿Qué tipo de cuenta?' : 'What type of account?'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setUserType('artist')}
                  className="border border-zinc-800 hover:border-zinc-600 p-8 transition-colors group"
                  data-testid="select-artist"
                >
                  <Palette className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors mb-4 mx-auto" />
                  <p className="font-['Cinzel'] text-white text-center">
                    {language === 'es' ? 'TATUADOR' : 'ARTIST'}
                  </p>
                  <p className="text-xs text-zinc-600 text-center mt-2">
                    {language === 'es' ? 'Comparte tu trabajo' : 'Share your work'}
                  </p>
                </button>
                <button
                  onClick={() => setUserType('client')}
                  className="border border-zinc-800 hover:border-zinc-600 p-8 transition-colors group"
                  data-testid="select-client"
                >
                  <User className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors mb-4 mx-auto" />
                  <p className="font-['Cinzel'] text-white text-center">
                    {language === 'es' ? 'CLIENTE' : 'CLIENT'}
                  </p>
                  <p className="text-xs text-zinc-600 text-center mt-2">
                    {language === 'es' ? 'Encuentra artistas' : 'Find artists'}
                  </p>
                </button>
              </div>
            </div>
          )}

          {!isLogin && userType === 'artist' && !artistType && (
            <div className="mb-10">
              <p className="text-sm uppercase tracking-wider text-zinc-500 mb-6">
                {language === 'es' ? '¿Qué tipo de tatuador?' : 'What type of artist?'}
              </p>
              <div className="grid grid-cols-1 gap-4">
                <button
                  onClick={() => setArtistType('independent_artist')}
                  className="border border-zinc-800 hover:border-zinc-600 p-6 transition-colors group flex items-center gap-4"
                  data-testid="select-independent"
                >
                  <Palette className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors" />
                  <div className="text-left">
                    <p className="font-['Cinzel'] text-white">
                      {language === 'es' ? 'TATUADOR INDEPENDIENTE' : 'INDEPENDENT ARTIST'}
                    </p>
                    <p className="text-xs text-zinc-600 mt-1">
                      {language === 'es' ? 'Trabajo por mi cuenta' : 'I work on my own'}
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => setArtistType('studio_artist')}
                  className="border border-zinc-800 hover:border-zinc-600 p-6 transition-colors group flex items-center gap-4"
                  data-testid="select-studio-artist"
                >
                  <Users className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors" />
                  <div className="text-left">
                    <p className="font-['Cinzel'] text-white">
                      {language === 'es' ? 'TATUADOR DE ESTUDIO' : 'STUDIO ARTIST'}
                    </p>
                    <p className="text-xs text-zinc-600 mt-1">
                      {language === 'es' ? 'Trabajo en un estudio' : 'I work at a studio'}
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => setArtistType('studio')}
                  className="border border-zinc-800 hover:border-zinc-600 p-6 transition-colors group flex items-center gap-4"
                  data-testid="select-studio"
                >
                  <Building2 className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors" />
                  <div className="text-left">
                    <p className="font-['Cinzel'] text-white">
                      {language === 'es' ? 'ESTUDIO' : 'STUDIO'}
                    </p>
                    <p className="text-xs text-zinc-600 mt-1">
                      {language === 'es' ? 'Tengo un estudio de tatuajes' : 'I own a tattoo studio'}
                    </p>
                  </div>
                </button>
              </div>
              <button
                type="button"
                onClick={() => setUserType('')}
                className="w-full text-center text-sm text-zinc-500 hover:text-white transition-colors mt-6"
              >
                {language === 'es' ? '← Cambiar tipo de cuenta' : '← Change account type'}
              </button>
            </div>
          )}

          {(isLogin || (userType === 'client') || (userType === 'artist' && artistType)) && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-zinc-500">
                    Nombre {userType === 'artist' ? 'artístico' : 'completo'}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-4 text-lg focus:border-zinc-100 focus-visible:ring-0 placeholder:text-zinc-700"
                    placeholder="Tu nombre"
                    data-testid="name-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-4 text-lg focus:border-zinc-100 focus-visible:ring-0 placeholder:text-zinc-700"
                  placeholder="tu@email.com"
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500">
                  Contraseña
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-4 text-lg focus:border-zinc-100 focus-visible:ring-0 placeholder:text-zinc-700"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
              </div>

              {!isLogin && userType === 'artist' && artistType && (
                <div className="space-y-2">
                  <Label htmlFor="location" className="text-xs uppercase tracking-wider text-zinc-500">
                    {language === 'es' ? 'Ubicación' : 'Location'}
                  </Label>
                  <Input
                    id="location"
                    name="location"
                    type="text"
                    value={formData.location}
                    onChange={handleChange}
                    className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-4 text-lg focus:border-zinc-100 focus-visible:ring-0 placeholder:text-zinc-700"
                    placeholder={language === 'es' ? 'Ciudad, País' : 'City, Country'}
                    data-testid="location-input"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-6 h-auto mt-8"
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin" />
                ) : (
                  isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'
                )}
              </Button>

              {!isLogin && userType && (
                <button
                  type="button"
                  onClick={() => {
                    if (userType === 'artist' && artistType) {
                      setArtistType('');
                    } else {
                      setUserType('');
                    }
                  }}
                  className="w-full text-center text-sm text-zinc-500 hover:text-white transition-colors mt-4"
                >
                  {userType === 'artist' && artistType
                    ? (language === 'es' ? '← Cambiar tipo de tatuador' : '← Change artist type')
                    : (language === 'es' ? '← Cambiar tipo de cuenta' : '← Change account type')}
                </button>
              )}
            </form>
          )}

          <div className="mt-10 pt-6 border-t border-zinc-900">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setUserType('');
                setArtistType('');
              }}
              className="text-sm text-zinc-500 hover:text-white transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin 
                ? (language === 'es' ? '¿No tienes cuenta? Regístrate' : "Don't have an account? Sign up")
                : (language === 'es' ? '¿Ya tienes cuenta? Inicia sesión' : 'Already have an account? Log in')}
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1598371839696-5c5bb00bdc28?crop=entropy&cs=srgb&fm=jpg&w=1200')`
        }}
      >
        <div className="w-full h-full bg-zinc-950/40" />
      </div>
    </div>
  );
};

export default Auth;
